/**
 * Slider Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 * HTML5 range input with accessible labeling
 */

import React, { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface SliderProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
  /**
   * Slider label (required for accessibility)
   */
  label?: string;

  /**
   * Show current value indicator
   */
  showValue?: boolean;

  /**
   * Value unit (e.g., 'px', '%', 'x')
   */
  valueUnit?: string;

  /**
   * Helper text
   */
  helperText?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Slider Component (HTML5 Range Input)
 *
 * Features:
 * - WCAG 2.1 AA compliant
 * - BFSG compliant (44x44px minimum touch target)
 * - Associated label for screen readers
 * - Value indicator support
 * - Dark/Light mode support
 *
 * @example
 * ```tsx
 * // Basic slider
 * <Slider
 *   label="Quality"
 *   min={1}
 *   max={100}
 *   value={80}
 *   onChange={(e) => setValue(Number(e.target.value))}
 * />
 *
 * // With value display
 * <Slider
 *   label="Upscale Factor"
 *   min={1.0}
 *   max={2.0}
 *   step={0.1}
 *   value={1.5}
 *   showValue
 *   valueUnit="x"
 *   onChange={(e) => setValue(Number(e.target.value))}
 * />
 * ```
 */
export const Slider = forwardRef<ElementRef<'input'>, SliderProps>(
  (
    {
      label,
      showValue = false,
      valueUnit = '',
      helperText,
      className,
      id,
      disabled,
      required,
      value,
      min = 0,
      max = 100,
      step = 1,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Generate unique ID if not provided
    const generatedId = React.useId();
    const sliderId = id || `slider-${generatedId}`;
    const helperId = `${sliderId}-helper`;

    return (
      <div className="w-full">
        {/* Label and Value */}
        {(label || showValue) && (
          <div className="flex items-center justify-between mb-2">
            {label && (
              <label
                htmlFor={sliderId}
                className={cn(
                  'text-sm font-medium',
                  'text-neutral-700 dark:text-neutral-300',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {label}
                {required && (
                  <span className="text-red-600 dark:text-red-400 ml-1" aria-label={t('common.required')}>
                    *
                  </span>
                )}
              </label>
            )}

            {showValue && value !== undefined && (
              <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                {value}{valueUnit}
              </span>
            )}
          </div>
        )}

        {/* Slider */}
        <input
          ref={ref}
          id={sliderId}
          type="range"
          disabled={disabled}
          required={required}
          value={value}
          min={min}
          max={max}
          step={step}
          className={cn(
            // Base styles
            'w-full h-2 rounded-lg appearance-none cursor-pointer',

            // Track
            'bg-neutral-200 dark:bg-neutral-700',

            // Thumb (webkit browsers)
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5',
            '[&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:bg-primary-600 dark:[&::-webkit-slider-thumb]:bg-primary-500',
            '[&::-webkit-slider-thumb]:cursor-pointer',
            '[&::-webkit-slider-thumb]:transition-transform',
            '[&::-webkit-slider-thumb]:hover:scale-110',

            // Thumb (Firefox)
            '[&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5',
            '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0',
            '[&::-moz-range-thumb]:bg-primary-600 dark:[&::-moz-range-thumb]:bg-primary-500',
            '[&::-moz-range-thumb]:cursor-pointer',
            '[&::-moz-range-thumb]:transition-transform',
            '[&::-moz-range-thumb]:hover:scale-110',

            // Focus styles (WCAG 2.1: 3:1 contrast minimum)
            'focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-offset-2',
            'focus-visible:ring-primary-500 dark:focus-visible:ring-offset-neutral-900',

            // Disabled state
            'disabled:cursor-not-allowed disabled:opacity-50',
            'disabled:[&::-webkit-slider-thumb]:cursor-not-allowed',
            'disabled:[&::-moz-range-thumb]:cursor-not-allowed',

            // Custom className
            className
          )}
          aria-describedby={helperText ? helperId : undefined}
          aria-required={required}
          aria-valuemin={typeof min === 'number' ? min : Number(min)}
          aria-valuemax={typeof max === 'number' ? max : Number(max)}
          aria-valuenow={typeof value === 'number' ? value : Number(value)}
          {...props}
        />

        {/* Helper Text */}
        {helperText && (
          <p
            id={helperId}
            className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Slider.displayName = 'Slider';
